import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';

const TeamRolesModule = () => {
  const [roles, setRoles] = useState([]);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '', is_board_role: false });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    const { data, error } = await supabase
      .from('team_roles')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      const existing = data || [];
      if (!existing || existing.length === 0) {
        // Seed default roles if none exist
        const defaults = [
          { name: 'Board Chair', description: 'Chair of the Board of Directors', is_board_role: true, display_order: 1, is_active: true },
          { name: 'Board Member', description: 'Member of the Board of Directors', is_board_role: true, display_order: 2, is_active: true },
          { name: 'Team Lead', description: 'Leads the core team operations', is_board_role: false, display_order: 3, is_active: true },
          { name: 'Head of Creative', description: 'Manages creative and marketing initiatives', is_board_role: false, display_order: 4, is_active: true },
          { name: 'Head of Content', description: 'Manages content creation and strategy', is_board_role: false, display_order: 5, is_active: true },
          { name: 'Head IT', description: 'Manages information technology and systems', is_board_role: false, display_order: 6, is_active: true },
          { name: 'Operations Manager', description: 'Manages operational activities', is_board_role: false, display_order: 7, is_active: true },
        ];
        const { data: inserted, error: insertErr } = await supabase.from('team_roles').insert(defaults).select();
        if (!insertErr) {
          setRoles(inserted || defaults);
          return;
        }
      }

      setRoles(existing);
    } else {
      // If the team_roles table doesn't exist in Supabase, fall back to local defaults
      console.error('Error fetching roles:', error);
      const msg = (error && error.message) || String(error);
      if (msg.includes("Could not find the table") || msg.includes('does not exist')) {
        const localDefaults = [
          { id: `local-boardchair`, name: 'Board Chair', description: 'Chair of the Board of Directors', is_board_role: true, display_order: 1, is_active: true },
          { id: `local-boardmember`, name: 'Board Member', description: 'Member of the Board of Directors', is_board_role: true, display_order: 2, is_active: true },
          { id: `local-teamlead`, name: 'Team Lead', description: 'Leads the core team operations', is_board_role: false, display_order: 3, is_active: true },
          { id: `local-creative`, name: 'Head of Creative', description: 'Manages creative and marketing initiatives', is_board_role: false, display_order: 4, is_active: true },
          { id: `local-content`, name: 'Head of Content', description: 'Manages content creation and strategy', is_board_role: false, display_order: 5, is_active: true },
          { id: `local-it`, name: 'Head IT', description: 'Manages information technology and systems', is_board_role: false, display_order: 6, is_active: true },
          { id: `local-ops`, name: 'Operations Manager', description: 'Manages operational activities', is_board_role: false, display_order: 7, is_active: true },
        ];
        setRoles(localDefaults);
        return;
      }
      // otherwise just log the error and leave roles empty
    }
  };

  const handleCreateRole = async () => {
    const name = newRole.name ? newRole.name.trim() : '';
    const description = newRole.description ? newRole.description.trim() : '';
    if (!name) {
      alert('Please enter a role name');
      return;
    }

    try {
      // compute safe display order (max existing + 1)
      const maxOrder = (roles || []).reduce((max, r) => Math.max(max, Number(r.display_order) || 0), -1);
      const display_order = maxOrder + 1;

      const { data, error } = await supabase
        .from('team_roles')
        .insert([{ name, description, is_board_role: newRole.is_board_role, display_order, is_active: true }])
        .select();

        if (error) {
          console.error('Create role error:', error);
          const msg = (error && error.message) || String(error);
          if (msg.includes("Could not find the table") || msg.includes('does not exist')) {
            // Create a local fallback role so the dashboard remains usable
            const localRole = { id: `local-${Date.now()}`, name, description, is_board_role: newRole.is_board_role, display_order, is_active: true };
            setRoles(prev => [...(prev || []), localRole]);
            setNewRole({ name: '', description: '', is_board_role: false });
            setRoleDialogOpen(false);
            alert('Created local role (database table missing). Please create the table in Supabase to persist changes.');
            return;
          }
          alert('Could not create role: ' + (error.message || JSON.stringify(error)));
          return;
        }

        const inserted = Array.isArray(data) ? data[0] : data;
        // refresh roles and select the new one
        await fetchRoles();
        setNewRole({ name: '', description: '', is_board_role: false });
        setRoleDialogOpen(false);
    } catch (err) {
      console.error('Unexpected error creating role:', err);
      alert('Unexpected error: ' + (err.message || String(err)));
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Are you sure you want to delete this role?')) return;

    if (roleId.startsWith('local-')) {
      setRoles(prev => prev.filter(r => r.id !== roleId));
      return;
    }

    // Check if role has team members
    const { data: members, error: checkError } = await supabase
      .from('team')
      .select('id')
      .eq('role_id', roleId)
      .limit(1);

    if (checkError) {
      alert('Error checking role: ' + checkError.message);
      return;
    }

    if (members && members.length > 0) {
      alert('Cannot delete role with existing team members. Please reassign or remove team members first.');
      return;
    }

    const { error } = await supabase
      .from('team_roles')
      .delete()
      .eq('id', roleId);

    if (error) {
      alert('Error deleting role: ' + error.message);
    } else {
      fetchRoles();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">Team Roles</h2>
          <p className="text-gray-600">Manage team roles and positions</p>
        </div>
        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Role
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Role</DialogTitle>
              <DialogDescription>
                Add a new team role or position.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="role-name">Role Name</Label>
                <Input
                  id="role-name"
                  value={newRole.name}
                  onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Senior Developer"
                />
              </div>
              <div>
                <Label htmlFor="role-description">Description (Optional)</Label>
                <Textarea
                  id="role-description"
                  value={newRole.description}
                  onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this role"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is-board-role"
                  checked={newRole.is_board_role}
                  onChange={(e) => setNewRole(prev => ({ ...prev, is_board_role: e.target.checked }))}
                />
                <Label htmlFor="is-board-role">Board Role</Label>
              </div>
              <Button onClick={handleCreateRole} className="w-full">
                Create Role
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {roles.map((role) => (
          <div key={role.id} className="flex justify-between items-center p-4 border border-gray-200 rounded-lg">
            <div>
              <div className="font-medium">{role.name}</div>
              {role.description && <div className="text-sm text-gray-500">{role.description}</div>}
              <div className="text-xs text-gray-400 mt-1">
                {role.is_board_role ? 'Board Role' : 'Team Role'}
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteRole(role.id)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamRolesModule;